import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileRejection } from 'react-dropzone'
import { TFunction } from 'i18next'
import {
  Alert,
  Avatar,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Grid,
  Stack,
  Divider,
  Button,
  Hidden,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import UploadIcon from '@mui/icons-material/UploadFile'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CloseIcon from '@mui/icons-material/Close'
import { LoadingButton } from '@mui/lab'
import { path } from 'ramda'

import { MapPinLaptop } from '@cibo/ui'

import { useUploadShapeFile } from '../../UploadShapeFile'

const InstructionStep = ({ stepNumber, t }: { stepNumber: number; t: TFunction }) => (
  <Stack key={stepNumber} direction="row" spacing={2} alignItems="center">
    <Avatar sx={{ bgcolor: 'secondary.main', width: 35, height: 35 }}>{stepNumber}</Avatar>
    <Box>
      <Typography variant="body1">{t(`step${stepNumber}head`)}</Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {t(`step${stepNumber}detail`)}
      </Typography>
    </Box>
  </Stack>
)

export type Props = {
  onUpload(r: any): void
}

type ErrorModalState = any

export const SelectFile = ({ onUpload }: Props) => {
  const { t } = useTranslation('@cibo/landmanager/features/UploadFlow')
  const [errorModalState, setErrorModalState] = useState<ErrorModalState | undefined>()
  const [nothingFoundModalState, setNothingFoundModalState] = useState(false)
  const [checked, setChecked] = useState<boolean>(false)

  // TODO these should do something
  const onBusy = () => null
  const handleError = () => null

  const handleFilesRejected = (rejections: FileRejection[]) => {
    setErrorModalState({ rejections })
  }
  const handleChange = () => {
    setChecked(val => !val)
  }

  const onUploadResponse = (response: any) => {
    const entries = path(['item', 'entries'])(response) as []

    if (entries.length) {
      onUpload(response)
    } else {
      setNothingFoundModalState(true)
    }
  }

  const {
    busy,
    dropzone: { getRootProps, getInputProps, isDragActive, open },
    error,
    handleUpload,
    excludeFile,
    includedAcceptedFiles,
  } = useUploadShapeFile(onBusy, handleError, onUploadResponse, handleFilesRejected)

  const handleRemoveFile = (filename: string) => () => excludeFile(filename)

  return (
    <div {...getRootProps()}>
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Typography variant="h4" color="text.secondary">
            {t('title')}
          </Typography>
          <Typography>{t('instructions')}</Typography>
          <Stack spacing={5} p={5}>
            <InstructionStep t={t} stepNumber={1} />
            <InstructionStep t={t} stepNumber={2} />
            <InstructionStep t={t} stepNumber={3} />
          </Stack>
        </Grid>
        <Hidden mdDown>
          <Grid item xs={4}>
            <MapPinLaptop />
          </Grid>
        </Hidden>
      </Grid>

      {isDragActive ? (
        <Box p={5} sx={{ border: '2px dashed gray' }} alignItems="center">
          <Grid container sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {t('drop')}
            </Typography>
          </Grid>
        </Box>
      ) : (
        <Box>
          <Divider />
          <Stack m={5} spacing={2} direction={{ xs: 'column', md: 'row' }} alignItems="center">
            {/* @ts-ignore types of property 'capture' are incompatible */}
            <input {...getInputProps()} data-testid="upload_input" />
            {!includedAcceptedFiles?.length ? (
              <>
                <Button variant="contained" onClick={open}>
                  {t('selectFiles')}
                </Button>
                <Typography>{t('or')}</Typography>
                <Stack spacing={2} direction="row" alignItems="center">
                  <UploadIcon sx={{ color: 'text.secondary', fontSize: 40 }} />
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {t('dropFileHere')}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t('buttonInstructions')}
                </Typography>
              </>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <LoadingButton
                    onClick={e => handleUpload(e, checked)}
                    disabled={!includedAcceptedFiles?.length}
                    loading={busy}
                    variant="contained"
                    data-testid="commit_shapefile"
                  >
                    {t('upload')}
                  </LoadingButton>
                  <Stack sx={{ alignItems: 'flex-start' }} spacing={1}>
                    {includedAcceptedFiles?.map((file: { name: string }) => (
                      <Chip
                        key={file.name}
                        label={file.name}
                        variant="outlined"
                        onDelete={handleRemoveFile(file.name)}
                        disabled={busy}
                      />
                    ))}

                    {!!error && <Alert severity="error">{t('uploadProblem')}</Alert>}
                  </Stack>
                </Stack>
                <FormControlLabel
                  control={<Checkbox checked={checked} onChange={handleChange} />}
                  label={
                    <Stack>
                      <Typography>{t('singleFieldFile')}</Typography>
                      <Typography variant="caption">{t('singleFieldFileExplainer')}</Typography>
                    </Stack>
                  }
                />
              </Stack>
            )}
          </Stack>

          <Divider />

          <Dialog open={!!nothingFoundModalState}>
            <IconButton
              onClick={() => setNothingFoundModalState(false)}
              sx={{ position: 'absolute', top: 0, right: 0 }}
            >
              <CloseIcon />
            </IconButton>

            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ErrorOutlineIcon color="error" sx={{ fontSize: 36 }} />
                <Typography variant="h4">{t('noFieldsFound')}</Typography>
              </Stack>
            </DialogTitle>

            <DialogContent>
              <Stack spacing={2} sx={{ paddingTop: 2 }}>
                <Typography>{t('nothingFound')}</Typography>
                <Typography variant="caption">{t('tryUploadAgain')}</Typography>
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setNothingFoundModalState(false)} variant="contained">
                {t('ok')}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={!!errorModalState}>
            <IconButton
              onClick={() => setErrorModalState(undefined)}
              sx={{ position: 'absolute', top: 0, right: 0 }}
            >
              <CloseIcon />
            </IconButton>

            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ErrorOutlineIcon color="error" sx={{ fontSize: 36 }} />
                <Typography variant="h4">{t('uploadFailed')}</Typography>
              </Stack>
            </DialogTitle>

            <DialogContent>
              <Stack spacing={2}>
                <Box>
                  {errorModalState?.rejections?.map(({ file }: FileRejection) => (
                    <Chip
                      color="error"
                      key={file.name}
                      label={file.name}
                      variant="outlined"
                      onDelete={() => null}
                    />
                  ))}
                </Box>

                <Typography>
                  {errorModalState?.rejections.length > 1 ? (
                    <>{t('multipleFilesError')}</>
                  ) : (
                    <>{t('fileTypeError')}</>
                  )}{' '}
                  {t('step1detail')}
                </Typography>

                <Typography variant="caption">{t('tryUploadAgain')}</Typography>
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setErrorModalState(undefined)} variant="contained">
                {t('ok')}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}
    </div>
  )
}
